import React, { useEffect, useState } from 'react';
import { 
  Search, 
  Filter, 
  Users, 
  CheckCircle, 
  XCircle, 
  Clock, 
  AlertTriangle,
  Eye,
  UserCheck,
  UserX,
  Pause,
  Play,
  MoreHorizontal
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAdvisorStore } from '../../store/advisorStore';
import { AdvisorStatusBadge } from './AdvisorStatusBadge';
import { AdvisorApprovalModal } from './AdvisorApprovalModal';
import { AdvisorRejectionModal } from './AdvisorRejectionModal';
import { AdvisorSuspensionModal } from './AdvisorSuspensionModal';
import type { AdvisorManagement as AdvisorManagementType, AdvisorFilters, AdvisorStatus } from '../../types/advisor';

export const AdvisorManagement: React.FC = () => {
  const navigate = useNavigate();
  const {
    advisors,
    stats,
    territories,
    sectors,
    isLoading,
    error,
    fetchAdvisors,
    fetchStats,
    fetchTerritories,
    fetchSectors,
    clearError
  } = useAdvisorStore();

  const [filters, setFilters] = useState<AdvisorFilters>({
    search: '',
    status: 'ALL',
    territory: '',
    sector: '',
    sort: 'recent',
    order: 'desc'
  });

  const [selectedAdvisor, setSelectedAdvisor] = useState<AdvisorManagementType | null>(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [showSuspensionModal, setShowSuspensionModal] = useState(false);

  useEffect(() => {
    fetchAdvisors(filters);
    fetchStats();
    fetchTerritories();
    fetchSectors();
    clearError();
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchAdvisors(filters);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [filters]);

  const handleFilterChange = (key: keyof AdvisorFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleApprove = (advisor: AdvisorManagementType) => {
    setSelectedAdvisor(advisor);
    setShowApprovalModal(true);
  };

  const handleReject = (advisor: AdvisorManagementType) => {
    setSelectedAdvisor(advisor);
    setShowRejectionModal(true);
  };

  const handleSuspend = (advisor: AdvisorManagementType) => {
    setSelectedAdvisor(advisor);
    setShowSuspensionModal(true);
  };

  const handleActivate = async (advisor: AdvisorManagementType) => {
    try {
      await useAdvisorStore.getState().activateAdvisor(advisor.id);
      // Refresh data after successful activation
      await fetchAdvisors(filters);
      await fetchStats();
    } catch (error) {
      console.error('Failed to activate advisor:', error);
    }
  };

  const getStatusStats = () => {
    if (!stats) return { pending: 0, active: 0, suspended: 0, rejected: 0 };
    
    return {
      pending: stats.pending_count,
      active: advisors.filter(a => a.advisor_status === 'ACTIVE').length,
      suspended: advisors.filter(a => a.advisor_status === 'SUSPENDED').length,
      rejected: stats.rejected_count
    };
  };

  const statusStats = getStatusStats();

  const getActionButtons = (advisor: AdvisorManagementType) => {
    const buttons = [];

    // View button
    buttons.push(
      <button
        key="view"
        onClick={() => navigate(`/admin/accompagnateurs/${advisor.id}`)}
        className="p-1 text-gray-400 hover:text-gray-600 rounded"
        title="Voir les détails"
      >
        <Eye className="w-4 h-4" />
      </button>
    );

    // Status-specific actions
    switch (advisor.advisor_status) {
      case 'PENDING':
        buttons.push(
          <button
            key="approve"
            onClick={() => handleApprove(advisor)}
            className="p-1 text-green-600 hover:text-green-700 rounded"
            title="Approuver"
          >
            <UserCheck className="w-4 h-4" />
          </button>,
          <button
            key="reject"
            onClick={() => handleReject(advisor)}
            className="p-1 text-red-600 hover:text-red-700 rounded"
            title="Refuser"
          >
            <UserX className="w-4 h-4" />
          </button>
        );
        break;
      
      case 'ACTIVE':
        buttons.push(
          <button
            key="suspend"
            onClick={() => handleSuspend(advisor)}
            className="p-1 text-orange-600 hover:text-orange-700 rounded"
            title="Suspendre"
          >
            <Pause className="w-4 h-4" />
          </button>
        );
        break;
      
      case 'SUSPENDED':
        buttons.push(
          <button
            key="activate"
            onClick={() => handleActivate(advisor)}
            className="p-1 text-green-600 hover:text-green-700 rounded"
            title="Réactiver"
          >
            <Play className="w-4 h-4" />
          </button>
        );
        break;
    }

    return buttons;
  };

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600">{error}</p>
          <button
            onClick={() => {
              clearError();
              fetchAdvisors(filters);
            }}
            className="mt-2 text-red-700 hover:text-red-800 font-medium"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestion des accompagnateurs</h1>
          <p className="text-gray-600 mt-1">
            Validation et suivi des accompagnateurs ESS
          </p>
        </div>
        
        <button
          onClick={() => navigate('/admin/accompagnateurs/requests')}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Clock className="w-4 h-4" />
          <span>Demandes en attente ({statusStats.pending})</span>
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">En attente</p>
              <p className="text-2xl font-bold text-orange-600">{statusStats.pending}</p>
            </div>
            <Clock className="w-8 h-8 text-orange-400" />
          </div>
        </div>
        
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Actifs</p>
              <p className="text-2xl font-bold text-green-600">{statusStats.active}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-400" />
          </div>
        </div>
        
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Suspendus</p>
              <p className="text-2xl font-bold text-orange-600">{statusStats.suspended}</p>
            </div>
            <AlertTriangle className="w-8 h-8 text-orange-400" />
          </div>
        </div>
        
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Refusés</p>
              <p className="text-2xl font-bold text-red-600">{statusStats.rejected}</p>
            </div>
            <XCircle className="w-8 h-8 text-red-400" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Rechercher..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Status Filter */}
          <select
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="ALL">Tous les statuts</option>
            <option value="PENDING">En attente</option>
            <option value="ACTIVE">Actifs</option>
            <option value="SUSPENDED">Suspendus</option>
            <option value="REJECTED">Refusés</option>
          </select>

          {/* Territory Filter */}
          <select
            value={filters.territory}
            onChange={(e) => handleFilterChange('territory', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">Tous les territoires</option>
            {territories.map((territory) => (
              <option key={territory.id} value={territory.name}>
                {territory.name}
              </option>
            ))}
          </select>

          {/* Sector Filter */}
          <select
            value={filters.sector}
            onChange={(e) => handleFilterChange('sector', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">Tous les secteurs</option>
            {sectors.map((sector) => (
              <option key={sector.id} value={sector.name}>
                {sector.name}
              </option>
            ))}
          </select>

          {/* Sort */}
          <select
            value={`${filters.sort}-${filters.order}`}
            onChange={(e) => {
              const [sort, order] = e.target.value.split('-');
              handleFilterChange('sort', sort);
              handleFilterChange('order', order);
            }}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="recent-desc">Plus récents</option>
            <option value="name-asc">Nom A-Z</option>
            <option value="name-desc">Nom Z-A</option>
            <option value="projects-desc">Plus de projets</option>
            <option value="completion-desc">Meilleur taux</option>
          </select>
        </div>
      </div>

      {/* Advisors Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : advisors.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun accompagnateur trouvé</h3>
            <p className="text-gray-500">
              {filters.search || filters.status !== 'ALL' || filters.territory || filters.sector
                ? 'Aucun accompagnateur ne correspond aux filtres sélectionnés.'
                : 'Aucun accompagnateur enregistré pour le moment.'
              }
            </p>
            {(filters.search || filters.status !== 'ALL' || filters.territory || filters.sector) && (
              <button
                onClick={() => setFilters({ search: '', status: 'ALL', territory: '', sector: '', sort: 'recent', order: 'desc' })}
                className="mt-4 text-blue-600 hover:text-blue-700 font-medium"
              >
                Réinitialiser les filtres
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Accompagnateur
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Territoire / Secteur
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Projets
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Performance
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {advisors.map((advisor) => (
                  <tr key={advisor.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                          <span className="text-white font-medium text-sm">
                            {advisor.full_name?.charAt(0) || '?'}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{advisor.full_name}</p>
                          <p className="text-sm text-gray-500">{advisor.email}</p>
                          {advisor.organization && (
                            <p className="text-xs text-gray-400">{advisor.organization}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <AdvisorStatusBadge status={advisor.advisor_status as AdvisorStatus} />
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{advisor.territory || 'Non spécifié'}</p>
                        <p className="text-sm text-gray-500">{advisor.sector || 'Non spécifié'}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {advisor.current_projects_count} actifs
                        </p>
                        <p className="text-sm text-gray-500">
                          {advisor.active_projects_count} total
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <div className="w-16 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-blue-600 h-2 rounded-full" 
                            style={{ width: `${Math.min(advisor.avg_completion_rate, 100)}%` }}
                          />
                        </div>
                        <span className="text-sm text-gray-600">
                          {Math.round(advisor.avg_completion_rate)}%
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-1">
                        {getActionButtons(advisor)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modals */}
      {selectedAdvisor && (
        <>
          <AdvisorApprovalModal
            isOpen={showApprovalModal}
            onClose={() => {
              setShowApprovalModal(false);
              setSelectedAdvisor(null);
            }}
            advisor={selectedAdvisor}
            onSuccess={() => {
              fetchAdvisors(filters);
              fetchStats();
            }}
          />
          
          <AdvisorRejectionModal
            isOpen={showRejectionModal}
            onClose={() => {
              setShowRejectionModal(false);
              setSelectedAdvisor(null);
            }}
            advisor={selectedAdvisor}
            onSuccess={() => {
              fetchAdvisors(filters);
              fetchStats();
            }}
          />
          
          <AdvisorSuspensionModal
            isOpen={showSuspensionModal}
            onClose={() => {
              setShowSuspensionModal(false);
              setSelectedAdvisor(null);
            }}
            advisor={selectedAdvisor}
            onSuccess={() => {
              fetchAdvisors(filters);
              fetchStats();
            }}
          />
        </>
      )}
    </div>
  );
};